import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import moment from 'moment';

import { FundDisplay } from '@gs-2.0/shared';
import { TransactionService } from '../../../_core/api/transaction.service';
import { CustomValidators } from '../../../_core/helpers/custom-validators.helpers';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import FundTypes from '../../../_core/constants/fund-types';
import { Fund } from '../../../_core/models/fund';
import { Configuration } from '../../../app.constants';

interface TransferFund {
  cseg5: string;
  fundName: string;
  fundNumber: string;
  fundNumberDisplay?: string;
  displayName: string;
}

@Component({
  selector: 'app-transfer-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  standalone: false,
})
export class TransferFormComponent implements OnInit, OnChanges {
  @Input() currentFund: Fund;
  @Input() userFunds: FundDisplay[] = [];
  @Input() displayCurrencySymbol: string | null = null;
  @Input() grantingCurrencyBalance: number | null = null;

  @Output() formChangedEvent = new EventEmitter<any>();
  @Output() formIsValid = new EventEmitter<boolean>();

  transferForm: FormGroup;
  formData: any;
  pageLoading = false;

  availableFunds: TransferFund[] = [];
  checkingFundNumber = false;
  verifiedReceiverCseg5: string | null = null;
  verifiedReceiverFundNumber: string | null = null;
  loadingFunds = false;

  /** Whether the My Fund/Other Fund radio is shown (only for DAF) */
  showFundRadio = false;
  /** Whether the "Other Fund" option is available */
  showOtherFundOption = false;
  /** Whether the My Fund dropdown is disabled (Project funds pre-select parent) */
  myFundDisabled = false;
  /** Whether to show info message when DAF user has no other DAF funds for My Fund */
  showOneFundInfo = false;

  today: Date;
  minDate: Date;

  fundTypes = FundTypes;
  instanceName: string;
  availableAmountToDisplay: number;
  zeroDecimalCurrency = false;

  dateFormatPrimeNG = 'mm/dd/yy';
  dateFormatPlaceholder = 'MM/dd/yyyy';
  displayAmount = '';
  decimalSymbol = '.';
  private digitGroupingSymbol = ',';
  private digitGrouping = '123,456,789';

  constructor(
    private fb: FormBuilder,
    private transactionService: TransactionService,
    private globalConfigApiService: GlobalConfigApiService,
    private config: Configuration
  ) {
    this.instanceName = this.config.name;
    this.loadGlobalConfig();
  }

  private loadGlobalConfig(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.applyFormat(config.response.numberFormat);
        }
        if (config?.response?.dateFormat) {
          this.dateFormatPlaceholder = config.response.dateFormat;
          this.dateFormatPrimeNG = config.response.dateFormat
            .replace(/yyyy/gi, 'yy')
            .replace(/MMMM/g, '\x02')
            .replace(/MMM/g, '\x01')
            .replace(/MM/g, 'mm')
            .replace(/\bM\b/g, 'm')
            .replace(/\x02/g, 'MM')
            .replace(/\x01/g, 'M');
        }
      },
    });
  }

  private applyFormat(numberFormat: any): void {
    this.decimalSymbol = numberFormat.decimalSymbol?.value || '.';
    this.digitGroupingSymbol = numberFormat.digitGroupingSymbol?.value || ',';
    this.digitGrouping = numberFormat.digitGrouping || '123,456,789';
  }

  formatNumberValue(value: number): string {
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGroupingFn(intPart);
    const result = grouped + this.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGroupingFn(intPart: string): string {
    const patternParts = this.digitGrouping.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;
    const groupSizes = patternParts.map(p => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;
    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }
    return groups.reverse().join(this.digitGroupingSymbol);
  }

  private parseFormattedValue(formatted: string): number | null {
    if (!formatted || formatted.trim() === '') return null;
    const escaped = this.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cleaned = formatted.replace(new RegExp(escaped, 'g'), '');
    const normalized = cleaned.replace(this.decimalSymbol, '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowedKeys.includes(event.key)) return;
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) return;
    if (/^\d$/.test(event.key)) return;
    if (event.key === this.decimalSymbol) {
      const target = event.target as HTMLInputElement;
      if (target.value.includes(this.decimalSymbol)) event.preventDefault();
      return;
    }
    event.preventDefault();
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPos = input.selectionStart || 0;
    const oldValue = input.value;

    const escapedGrouping = this.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let raw = oldValue.replace(new RegExp(escapedGrouping, 'g'), '');

    const decIdx = raw.indexOf(this.decimalSymbol);
    let intRaw: string;
    let decRaw: string | undefined;

    if (decIdx >= 0) {
      intRaw = raw.substring(0, decIdx).replace(/\D/g, '');
      decRaw = raw.substring(decIdx + 1).replace(/\D/g, '').substring(0, 2);
    } else {
      intRaw = raw.replace(/\D/g, '');
    }

    intRaw = intRaw.replace(/^0+(?=\d)/, '');
    const grouped = intRaw ? this.applyDigitGroupingFn(intRaw) : '';

    let formatted: string;
    if (decIdx >= 0) {
      formatted = grouped + this.decimalSymbol + (decRaw ?? '');
    } else {
      formatted = grouped;
    }

    let digitsBefore = 0;
    for (let i = 0; i < cursorPos && i < oldValue.length; i++) {
      if (oldValue[i] !== this.digitGroupingSymbol) digitsBefore++;
    }

    let newPos = 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== this.digitGroupingSymbol) count++;
      if (count === digitsBefore) { newPos = i + 1; break; }
    }
    if (count < digitsBefore) newPos = formatted.length;

    let parsed = this.parseFormattedValue(formatted);

    // // Enforce max value
    // if (parsed !== null && this.availableAmountToDisplay && parsed > this.availableAmountToDisplay) {
    //   parsed = this.availableAmountToDisplay;
    //   formatted = this.formatNumberValue(parsed);
    //   newPos = formatted.length;
    // }

    input.value = formatted;
    input.setSelectionRange(newPos, newPos);
    this.displayAmount = formatted;

    this.amount.patchValue(parsed, { emitEvent: true });
  }

  onAmountBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    let parsed = this.parseFormattedValue(input.value);

    // // Enforce max value
    // if (parsed !== null && this.availableAmountToDisplay && parsed > this.availableAmountToDisplay) {
    //   parsed = this.availableAmountToDisplay;
    // }

    this.amount.markAsTouched();
    this.amount.patchValue(parsed, { emitEvent: true });
    if (parsed !== null) {
      this.displayAmount = this.formatNumberValue(parsed);
      input.value = this.displayAmount;
    } else {
      this.displayAmount = '';
    }
  }

  ngOnInit(): void {
    if (!this.currentFund) return;
    this.today = moment().startOf('day').toDate();
    this.minDate = moment().startOf('day').add(7, 'days').toDate();
    this.computeAvailableAmountToDisplay();
    this.createForm();
    this.transferForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
    this.loadAvailableFunds();
    this.loadCurrencyMinimumAmount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFund']) {
      this.currentFund = changes['currentFund'].currentValue;
      if (this.currentFund && !this.transferForm) {
        this.today = moment().startOf('day').toDate();
        this.minDate = moment().startOf('day').add(7, 'days').toDate();
        this.computeAvailableAmountToDisplay();
        this.createForm();
        this.transferForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
        this.loadAvailableFunds();
        this.loadCurrencyMinimumAmount();
      } else if (this.currentFund && this.transferForm) {
        // Fund changed via switcher — fully reset the form for the new fund
        this.verifiedReceiverCseg5 = null;
        this.verifiedReceiverFundNumber = null;
        this.checkingFundNumber = false;
        this.zeroDecimalCurrency = false;
        this.computeAvailableAmountToDisplay();

        // Recreate the form to reset all values and validators for new fund type
        this.createForm();
        this.transferForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.emitChanges());
        this.loadAvailableFunds();
        this.loadCurrencyMinimumAmount();
      }
    }
    if (changes['displayCurrencySymbol'] && this.transferForm) {
      this.loadCurrencyMinimumAmount();
    }
    if (changes['grantingCurrencyBalance'] && this.transferForm) {
      this.computeAvailableAmountToDisplay();
      this.amount.setValidators([
        CustomValidators.required,
        CustomValidators.maxAmountAllowed(this.availableAmountToDisplay),
      ]);
      this.amount.updateValueAndValidity();
    }
    if (changes['userFunds'] && this.currentFund) {
      this.loadAvailableFunds();
    }
  }

  createForm(): void {
    this.transferForm = this.fb.group({
      receiverFundType: ['myFund'],
      myFund: [null],
      otherFundName: [''],
      otherFundNumber: [''],
      amount: [
        null,
        [CustomValidators.required, CustomValidators.maxAmountAllowed(this.availableAmountToDisplay)],
      ],
      timing: ['', [Validators.required]],
      date: [''],
      showAdditionalNotes: [false],
      additionalNotes: [''],
    });

    this.receiverFundType.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => this.handleReceiverFundTypeChange(value));

    this.timing.valueChanges.subscribe((value) => this.handleTimingChange(value));

    this.showAdditionalNotes.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => this.handleAdditionalNotesToggle(value));

    this.amount.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      if (value !== null && value <= 0) {
        this.amount.patchValue(0, { emitEvent: false });
        return;
      }

      const errors = { ...this.amount.errors };
      delete errors['noDecimals'];

      if (value && this.zeroDecimalCurrency && !Number.isInteger(value)) {
        errors['noDecimals'] = true;
      }

      this.amount.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
      this.emitChanges();
    });

    this.otherFundNumber.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((value) => {
          this.verifiedReceiverCseg5 = null;
          this.verifiedReceiverFundNumber = null;
          if (!value) {
            this.otherFundName.patchValue('', { emitEvent: false });
            return false;
          }
          // Skip API call if sync validators already failed
          const syncErrors = this.otherFundNumber.errors;
          if (syncErrors?.['specialChars'] || syncErrors?.['sameFund']) return false;
          return true;
        }),
        switchMap((value) => {
          this.checkingFundNumber = true;
          return this.transactionService.searchFundNumber(value).pipe(
            catchError((err) => {
              if (err?.status === 404 || err?.error?.message === 'FUNDNUMBER_NOT_FOUND') {
                return of({ message: 'FUNDNUMBER_NOT_FOUND' });
              }
              if (err?.status === 409) {
                if (err?.error?.message === 'FORM_VALIDATORS_OTHER_FUND_CHARITY') {
                  return of({ message: 'FUND_IS_CHARITY' });
                }
                return of({ message: 'INELIGIBLE_FUND' });
              }
              return of({ error: true });
            })
          );
        })
      )
      .subscribe((res: any) => {
        this.checkingFundNumber = false;
        const errors = { ...this.otherFundNumber.errors };
        delete errors['fundNotFound'];
        delete errors['serverError'];
        delete errors['fundIsCharity'];
        delete errors['ineligibleFund'];

        if (res?.error) {
          errors['serverError'] = true;
          this.verifiedReceiverCseg5 = null;
          this.verifiedReceiverFundNumber = null;
          this.otherFundName.patchValue('', { emitEvent: false });
        } else if (res?.message === 'FUNDNUMBER_NOT_FOUND') {
          errors['fundNotFound'] = true;
          this.verifiedReceiverCseg5 = null;
          this.verifiedReceiverFundNumber = null;
          this.otherFundName.patchValue('', { emitEvent: false });
        } else if (res?.message === 'FUND_IS_CHARITY') {
          errors['fundIsCharity'] = true;
          this.verifiedReceiverCseg5 = null;
          this.verifiedReceiverFundNumber = null;
          this.otherFundName.patchValue('', { emitEvent: false });
        } else if (res?.message === 'INELIGIBLE_FUND') {
          errors['ineligibleFund'] = true;
          this.verifiedReceiverCseg5 = null;
          this.verifiedReceiverFundNumber = null;
          this.otherFundName.patchValue('', { emitEvent: false });
        } else if (res?.cseg5 || res?.fundNumber) {
          this.verifiedReceiverCseg5 = res.cseg5 ?? null;
          this.verifiedReceiverFundNumber = res.fundNumber ?? null;
          this.otherFundName.patchValue(res.fundName || '', { emitEvent: false });
        }

        this.otherFundNumber.setErrors(Object.keys(errors).length ? errors : null);
        this.emitChanges();
      });

    // DAF defaults to otherFund; non-DAF funds always use myFund
    const defaultReceiverType = this.currentFund?.fundType === FundTypes.DAF ? 'otherFund' : 'myFund';
    this.receiverFundType.patchValue(defaultReceiverType, { emitEvent: false });
    this.handleReceiverFundTypeChange(defaultReceiverType);
  }

  handleReceiverFundTypeChange(value: string): void {
    if (value === 'myFund') {
      this.myFund.setValidators([Validators.required]);
      this.otherFundName.enable();
      this.otherFundName.clearValidators();
      this.otherFundNumber.clearValidators();
      this.otherFundName.patchValue('');
      this.otherFundNumber.patchValue('');
    } else {
      this.myFund.clearValidators();
      this.myFund.patchValue(null);
      this.otherFundName.clearValidators();
      this.otherFundName.disable();
      this.otherFundNumber.setValidators([
        Validators.required,
        CustomValidators.specialChars,
        CustomValidators.sameFund(this.currentFund.fundNumber)
      ]);
    }
    this.myFund.updateValueAndValidity();
    this.otherFundName.updateValueAndValidity();
    this.otherFundNumber.updateValueAndValidity();
    this.emitChanges();
  }

  handleTimingChange(value: string): void {
    if (value === 'specificDate') {
      this.date.setValidators([Validators.required]);
    } else {
      this.date.patchValue('');
      this.date.clearValidators();
    }
    this.date.updateValueAndValidity();
  }

  handleAdditionalNotesToggle(value: boolean): void {
    if (value) {
      this.additionalNotes.setValidators([
        Validators.required,
        CustomValidators.minLength(10),
        CustomValidators.maxLength(250),
      ]);
    } else {
      this.additionalNotes.patchValue('');
      this.additionalNotes.clearValidators();
    }
    this.additionalNotes.updateValueAndValidity();
  }

  /** Identifies a fund the same way FundService.getFundIdFromFund() does, so comparisons stay
   * correct even when cseg5 is missing (falls back to fundNumber, matching the pattern already
   * used to build FundDisplay.id upstream). */
  private getFundIdentifier(fund: any): string {
    return fund?.id || fund?.cseg5 || fund?.fundNumber || '';
  }

  loadAvailableFunds(): void {
    if (!this.getFundIdentifier(this.currentFund)) return;
    const fundType = this.currentFund.fundType;

    if (fundType === FundTypes.PROJECT) {
      // Project funds can only transfer to their parent fund
      this.showFundRadio = false;
      this.showOtherFundOption = false;
      this.myFundDisabled = true;
      this.showOneFundInfo = false;

      // Find the parent fund: look through userFunds for the fund whose projects contain this project
      const parentFundDisplay = (this.userFunds || []).find((f) => {
        const rawFund = f.data;
        if (!rawFund?.projects) return false;
        return rawFund.projects.some((p: any) => this.getFundIdentifier(p) === this.getFundIdentifier(this.currentFund));
      });

      if (parentFundDisplay) {
        const parent = parentFundDisplay.data || parentFundDisplay;
        const fund: TransferFund = {
          cseg5: parent.cseg5 || parentFundDisplay.id || '',
          fundName: parent.fundName || parentFundDisplay.name || '',
          fundNumber: parent.fundNumber || parentFundDisplay.accountNumber || '',
          fundNumberDisplay: parent.fundNumberDisplay || parentFundDisplay.accountNumber || '',
          displayName: `${parent.fundName || parentFundDisplay.name || ''} (#${
            parent.fundNumberDisplay || parent.fundNumber || parentFundDisplay.accountNumber || ''
          })`,
        };
        this.availableFunds = [fund];
        this.myFund.patchValue(fund);
        this.myFund.disable();
      } else if (this.currentFund.parentFund) {
        // Fallback to parentFund if already set
        const parent = this.currentFund.parentFund;
        const fund: TransferFund = {
          cseg5: (parent as any).cseg5 || (parent as any).internalId || '',
          fundName: (parent as any).fundName || (parent as any).name || '',
          fundNumber: (parent as any).fundNumber || '',
          fundNumberDisplay: (parent as any).fundNumberDisplay || '',
          displayName: `${(parent as any).fundName || (parent as any).name || ''} (#${
            (parent as any).fundNumberDisplay || (parent as any).fundNumber || ''
          })`,
        };
        this.availableFunds = [fund];
        this.myFund.patchValue(fund);
        this.myFund.disable();
      } else {
        this.availableFunds = [];
      }
    } else if (fundType === FundTypes.CHARITY) {
      this.showFundRadio = false;
      this.myFundDisabled = false;
      this.showOneFundInfo = false;
      this.showOtherFundOption = false;
      this.loadingFunds = true;
      this.availableFunds = [];
      const cseg5 = this.currentFund.cseg5 || this.getFundIdentifier(this.currentFund);
      this.transactionService.getFundsForTransfer(cseg5).subscribe({
        next: (funds: any[]) => {
          this.availableFunds = (funds || []).map((f) => ({
            cseg5: f.cseg5 || '',
            fundName: f.fundName || '',
            fundNumber: f.fundNumber || '',
            fundNumberDisplay: f.fundNumberDisplay || '',
            displayName: `${f.fundName || ''} (#${f.fundNumberDisplay || f.fundNumber || ''})`,
          }));
          this.showOtherFundOption = this.availableFunds.length === 0;
          this.loadingFunds = false;
        },
        error: () => {
          this.availableFunds = [];
          this.showOtherFundOption = true;
          this.loadingFunds = false;
        },
      });
    } else {
      // DAF funds: use other DAF funds from user info, show My Fund/Other Fund radio
      this.showFundRadio = true;
      this.showOtherFundOption = true;
      this.myFundDisabled = false;
      this.showOneFundInfo = false;
      this.availableFunds = (this.userFunds || [])
        .filter((f) => {
          const fFundType = f.data?.fundType;
          return fFundType === FundTypes.DAF &&
            f.id !== this.getFundIdentifier(this.currentFund);
        })
        .map((f) => ({
          cseg5: f.data?.cseg5 || f.id,
          fundName: f.data?.fundName || f.name,
          fundNumber: f.data?.fundNumber || f.accountNumber,
          fundNumberDisplay: f.data?.fundNumberDisplay || f.accountNumber,
          displayName: `${f.data?.fundName || f.name} (#${f.data?.fundNumberDisplay || f.data?.fundNumber || f.accountNumber})`,
        }));

      // Show info message when user has no other DAF funds
      if (this.availableFunds.length === 0) {
        this.showOneFundInfo = true;
      }
    }
  }

  onMyFundChange(event: any): void {
    this.emitChanges();
  }

  computeAvailableAmountToDisplay(): void {
    const balance = this.grantingCurrencyBalance ?? this.currentFund.availableAmount;
    if (balance) {
      this.availableAmountToDisplay = SharedMethodsHelpers.computeAmountToDisplay(balance.toString());
    } else {
      this.availableAmountToDisplay = 0;
    }
  }

  emitChanges(): void {
    this.computeFormData();
    const isReceiverValid = this.isReceiverValid();
    const isValid = isReceiverValid && this.transferForm.valid;
    this.formIsValid.emit(isValid);
    this.formChangedEvent.emit(this.formData);
  }

  isReceiverValid(): boolean {
    const receiverType = this.receiverFundType.value;
    if (receiverType === 'myFund') {
      return !!this.myFund.value;
    } else {
      return (this.otherFundName.valid || this.otherFundName.disabled) && !!(this.verifiedReceiverCseg5 || this.verifiedReceiverFundNumber) && !!this.otherFundNumber.value;
    }
  }

  computeFormData(): void {
    const receiverType = this.receiverFundType.value;
    let receiverCseg5: string = null;
    let receiverFundName: string = null;
    let receiverFundNumber: string = null;

    if (receiverType === 'myFund' && this.myFund.value) {
      const selectedFund = this.myFund.value;
      receiverCseg5 = selectedFund.cseg5;
      receiverFundName = selectedFund.fundName;
      receiverFundNumber = selectedFund.fundNumber;
    } else if (receiverType === 'otherFund') {
      receiverCseg5 = this.verifiedReceiverCseg5;
      receiverFundName = this.otherFundName.value;
      receiverFundNumber = this.otherFundNumber.value;
    }

    this.formData = {
      ...this.transferForm.getRawValue(),
      receiverCseg5,
      receiverFundName,
      receiverFundNumber,
    };
  }

  availableAmountFloor(): number {
    return SharedMethodsHelpers.floor(this.currentFund.availableAmount);
  }

  private loadCurrencyMinimumAmount(): void {
    this.transactionService.checkMinimumAmount(this.displayCurrencySymbol || this.currentFund.currencySymbol).subscribe({
      next: (res: any) => {
        this.zeroDecimalCurrency = res?.zeroDecimalCurrency ?? false;
      },
      error: () => {},
    });
  }

  // Form getters
  get receiverFundType(): AbstractControl {
    return this.transferForm.get('receiverFundType');
  }

  get myFund(): AbstractControl {
    return this.transferForm.get('myFund');
  }

  get otherFundName(): AbstractControl {
    return this.transferForm.get('otherFundName');
  }

  get otherFundNumber(): AbstractControl {
    return this.transferForm.get('otherFundNumber');
  }

  get amount(): AbstractControl {
    return this.transferForm.get('amount');
  }

  get timing(): AbstractControl {
    return this.transferForm.get('timing');
  }

  get date(): AbstractControl {
    return this.transferForm.get('date');
  }

  get showAdditionalNotes(): AbstractControl {
    return this.transferForm.get('showAdditionalNotes');
  }

  get additionalNotes(): AbstractControl {
    return this.transferForm.get('additionalNotes');
  }
}
